/**
 * SSE 通知服务
 * 使用 SSE 接收实时通知推送
 */

import type { UnreadNotification } from './notificationService';

const BACKEND_URL = (window as any).AGIMX_CONFIG?.AI_CHAT_BACKEND_URL;

/**
 * SSE 通知事件类型
 */
export type NotificationSSEEventType = 
  | 'connected'
  | 'notification' 
  | 'read' 
  | 'read-all' 
  | 'heartbeat'
  | 'title-updated';

  /**
   * 标题更新事件数据
   */
  export interface TitleUpdateEvent {
    readonly conversationId: number;
    readonly title: string;
  }

/**
 * SSE 通知客户端配置
 */
export interface NotificationSSEClientOptions {
  /** 收到新通知 */
  readonly onNotification?: (notification: UnreadNotification) => void;
  /** 通知被标记为已读 */
  readonly onRead?: (taskId: number) => void;
  /** 全部标记已读 */
  readonly onReadAll?: (taskIds: readonly number[]) => void;
  /** 连接成功 */
  readonly onConnected?: () => void;
  /** 连接断开 */
  readonly onDisconnect?: () => void;
  /** 发生错误 */
  readonly onError?: (error: Error) => void;
  /** 标题更新 */
  readonly onTitleUpdate?: (event: TitleUpdateEvent) => void;
  /** 用户 ID（可选） */
  readonly userId?: string;
  /** 业务类型，用于服务端过滤通知 */
  readonly businessType?: string;
}

/**
 * SSE 通知客户端
 */
export interface NotificationSSEClient {
  /** 关闭连接 */
  readonly close: () => void;
  /** 连接是否已关闭 */
  readonly isClosed: boolean;
  /** 重新连接 */
  readonly reconnect: () => void;
}

/**
 * 创建 SSE 通知客户端
 */
export const createNotificationSSEClient = (
  options: NotificationSSEClientOptions
): NotificationSSEClient => {
  const { 
    onNotification, 
    onRead, 
    onReadAll, 
    onConnected, 
    onDisconnect,
    onError,
    onTitleUpdate,
    userId,
    businessType
  } = options;

  let closed = false;
  let eventSource: EventSource | null = null;
  let reconnectTimer: NodeJS.Timeout | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 秒

  const connect = (): void => {
    if (closed) return;

    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    if (businessType) params.set('businessType', businessType);
    const qs = params.toString();
    const url = qs
      ? `${BACKEND_URL}/api/notifications/stream?${qs}`
      : `${BACKEND_URL}/api/notifications/stream`;

    try {
      eventSource = new EventSource(url, { withCredentials: true }); // ✅ 携带 Cookie

      eventSource.onopen = () => {
        console.log('[NotificationSSE] 连接已建立');
        reconnectAttempts = 0; // 重置重连计数
      };

      // 连接成功事件
      eventSource.addEventListener('connected', () => {
        if (closed) return;
        onConnected?.();
      });

      // 新通知事件
      eventSource.addEventListener('notification', (event: MessageEvent) => {
        if (closed) return;
        try {
          const data = JSON.parse(event.data) as UnreadNotification;
          onNotification?.(data);
        } catch (err) {
          console.error('[NotificationSSE] 解析 notification 失败:', err);
        }
      });

      // 已读事件
      eventSource.addEventListener('read', (event: MessageEvent) => {
        if (closed) return;
        try {
          const data = JSON.parse(event.data) as { taskId: number };
          onRead?.(data.taskId);
        } catch (err) {
          console.error('[NotificationSSE] 解析 read 失败:', err);
        }
      });

      // 全部已读事件
      eventSource.addEventListener('read-all', (event: MessageEvent) => {
        if (closed) return;
        try {
          const data = JSON.parse(event.data) as { taskIds: readonly number[] };
          onReadAll?.(data.taskIds);
        } catch (err) {
          console.error('[NotificationSSE] 解析 read-all 失败:', err);
        }
      });

      // 新增：标题更新事件
      eventSource.addEventListener('title-updated', (event: MessageEvent) => {
        if (closed) return;
        try {
          const data = JSON.parse(event.data) as TitleUpdateEvent;
          console.log('[NotificationSSE] 收到标题更新:', data);
          onTitleUpdate?.(data);
        } catch (err) {
          console.error('[NotificationSSE] 解析 title-updated 失败:', err);
        }
      });

      // 心跳事件（保持连接）
      eventSource.addEventListener('heartbeat', () => {
        // 心跳不需要处理，只是保持连接
      });

      // 连接错误
      eventSource.onerror = () => {
        if (closed) return;

        console.error('[NotificationSSE] 连接断开');
        eventSource?.close();
        eventSource = null;
        onDisconnect?.();

        // 自动重连
        scheduleReconnect();
      };

    } catch (err) {
      console.error('[NotificationSSE] 创建连接失败:', err);
      onError?.(err instanceof Error ? err : new Error(String(err)));
      scheduleReconnect();
    }
  };

  const scheduleReconnect = (): void => {
    if (closed || reconnectAttempts >= maxReconnectAttempts) {
      if (reconnectAttempts >= maxReconnectAttempts) {
        console.error('[NotificationSSE] 达到最大重连次数，停止重连');
        onError?.(new Error('SSE 连接失败，已达到最大重连次数'));
      }
      return;
    }

    // 指数退避重连
    const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
    reconnectAttempts++;

    console.log(`[NotificationSSE] ${delay}ms 后尝试第 ${reconnectAttempts} 次重连`);

    reconnectTimer = setTimeout(() => {
      connect();
    }, delay);
  };

  const close = (): void => {
    if (closed) return;
    closed = true;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }

    console.log('[NotificationSSE] 连接已关闭');
  };

  const reconnect = (): void => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    reconnectAttempts = 0;
    closed = false;
    connect();
  };

  // 立即连接
  connect();

  return {
    close,
    get isClosed() {
      return closed;
    },
    reconnect
  };
};